import Button from '@amzn/awsui-components-core/button';
import ButtonDropdown from '@amzn/awsui-components-core/button-dropdown';
import { applyMode, Mode } from '@amzn/awsui-global-styles';
import { faExternalLinkAlt, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useMatch, useLocation } from 'react-router-dom';
import { eLocalStorageKeys, generateProfilePictureUrl, QUICKSIGHT_HOME_URL } from 'src/constants/AppConstants';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { isEmbedReadyMessage, postMessageToEmbeddedIframe } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import DFP_HEADER_LOGO from 'src/images/FinSuite-logo.png';
import GRID_ICON from 'src/images/grid-icon.svg';
import { ApplicationFlatTableEntity } from '../context/AppContextModels';
import { useAppContext } from '../context/AppContextProvider';
import { useAuth } from '../context/AuthContextProvider';
import './DFPTopNavigation.scss';
import GlobalDropdown from './GlobalDropdown';
import { GlobalSearchBar } from './GlobalSearchBar';
import { PROFILE_DROPDOWN_ITEMS } from './ProfileDropdownConstants';
import { ROUTE_CONFIG } from 'src/utilities/RouteConfig';

export const DFPTopNavigation: React.FC = () => {
  const navigate = useNavigate();
  const { Alias: userAlias } = useAuth();
  const { favoriteApplications, parsedApplications } = useAppContext();
  const activeParsedApps = useMemo(() => parsedApplications.filter((app: ApplicationFlatTableEntity) => app.isActive), [parsedApplications]);
  const [isDarkModeEnabled, setIsDarkModeEnabled] = usePersistedState(eLocalStorageKeys.IsDarkModeEnabled, false);
  const [showGlobalDropdownModal, setShowGlobalDropdownModal] = useState(false);
  const [favoriteApplicationsList, setFavoriteApplicationsList] = useState<ApplicationFlatTableEntity[]>([]);

  const embedMatch = useMatch('/all-apps/:id');
  const { pathname } = useLocation();

  // Tracks whether the currently embedded app supports postMessage navigation (handshake protocol)
  const embedReadyRef = useRef(false);

  // Listen for FINSUITE_EMBED_READY from the embedded iframe.
  // When received, we know the app supports postMessage navigation and can avoid remounting.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isEmbedReadyMessage(event)) {
        embedReadyRef.current = true;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Reset readiness when navigating away from an embedded app
  useEffect(() => {
    if (!embedMatch?.params?.id) {
      embedReadyRef.current = false;
    }
  }, [embedMatch?.params?.id]);

  useEffect(() => {
    applyMode(isDarkModeEnabled ? Mode.Dark : Mode.Light);
  }, [isDarkModeEnabled]);

  useEffect(() => {
    const _favoriteApplicationsList: ApplicationFlatTableEntity[] = activeParsedApps.filter((app: ApplicationFlatTableEntity) =>
      favoriteApplications.includes(app.id)
    );
    setFavoriteApplicationsList(_favoriteApplicationsList);
  }, [favoriteApplications, activeParsedApps]);

  const toggleGlobalDropdown = () => {
    setShowGlobalDropdownModal((prev) => !prev);
  };

  const embeddedApp = useMemo(() => {
    if (!embedMatch?.params?.id) return null;
    const app = parsedApplications.filter((a) => a.isActive).find((a) => a.id === embedMatch.params.id);
    if (!app || !app.embeddingEnabled) return null;
    return {
      url: app.applicationLink || null,
      name: app.applicationName || null
    };
  }, [embedMatch, parsedApplications]);

  /**
   * Sends a postMessage to the embedded iframe if it supports the protocol.
   * Returns true if the message was sent, false otherwise.
   */
  const sendMessageToEmbed = useCallback((type: string, payload?: Record<string, unknown>): boolean => {
    if (!embedReadyRef.current) return false;
    return postMessageToEmbeddedIframe(type, payload);
  }, []);

  const handleEmbeddedAppHomeNavigation = useCallback(() => {
    if (sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME)) return;
    // Fallback: remount the iframe (for apps that don't implement the protocol)
    if (embedMatch?.params?.id) {
      navigate(`/all-apps/${embedMatch.params.id}?t=${Date.now()}`);
    }
  }, [embedMatch?.params?.id, navigate, sendMessageToEmbed]);

  const handleDarkModeToggle = useCallback(() => {
    const newDarkMode = !isDarkModeEnabled;
    setIsDarkModeEnabled(newDarkMode);
    sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: newDarkMode });
  }, [isDarkModeEnabled, setIsDarkModeEnabled, sendMessageToEmbed]);

  const routeConfig = ROUTE_CONFIG[pathname];
  const displayAppName =
    embeddedApp?.name?.toLowerCase() ?? (routeConfig?.navigationSection === 'org-tools' ? routeConfig.navigationText?.toLowerCase() : null) ?? null;

  return (
    <>
      <GlobalDropdown
        showModal={showGlobalDropdownModal}
        onCancel={() => setShowGlobalDropdownModal(false)}
        onConfirm={() => setShowGlobalDropdownModal(false)}
        favoriteApplications={favoriteApplicationsList}
        parsedApplications={activeParsedApps}
      />
      <div id="h">
        <nav className="dfp-top-nav" aria-label="Main navigation">
          <div className="dfp-top-nav__left">
            <a
              href="/"
              className="dfp-top-nav__logo-link"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
              aria-label="FinSuite Home"
            >
              <img src={DFP_HEADER_LOGO} alt="FinSuite Logo" className="dfp-top-nav__logo" />
            </a>
            {displayAppName && (
              <>
                <span className="dfp-top-nav__divider" />
                {embeddedApp && embedMatch?.params?.id ? (
                  <a
                    href={`/all-apps/${embedMatch.params.id}`}
                    className="dfp-top-nav__app_text"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEmbeddedAppHomeNavigation();
                    }}
                    aria-label={`Navigate to ${embeddedApp.name} home`}
                  >
                    {displayAppName}
                  </a>
                ) : routeConfig?.path ? (
                  <a href={routeConfig.path} className="dfp-top-nav__app_text" aria-label={`Navigate to ${routeConfig.navigationText} home`}>
                    {displayAppName}
                  </a>
                ) : (
                  <div className="dfp-top-nav__app_text">{displayAppName}</div>
                )}
              </>
            )}
          </div>
          <div className="dfp-top-nav__center">
            <button
              className={`dfp-top-nav__grid-btn ${showGlobalDropdownModal ? 'dfp-top-nav__grid-btn--active' : ''}`}
              onClick={toggleGlobalDropdown}
              aria-label={showGlobalDropdownModal ? 'Close app menu' : 'Open app menu'}
              aria-expanded={showGlobalDropdownModal}
            >
              <img src={GRID_ICON} alt="" className="dfp-top-nav__grid-icon" />
            </button>
            <GlobalSearchBar />
          </div>
          <div className="dfp-top-nav__right">
            <span className="gen-ai-gradient-button">
              <Button iconName="gen-ai" variant="primary" onClick={() => window.open(QUICKSIGHT_HOME_URL, '_blank', 'noopener,noreferrer')}>
                Ask Quick
              </Button>
            </span>
            {embeddedApp?.url && (
              <button
                className="dfp-top-nav__utility-btn"
                onClick={() => embeddedApp.url && window.open(embeddedApp.url, '_blank', 'noopener,noreferrer')}
                aria-label={`Open ${embeddedApp.name || 'application'} in a new tab`}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </button>
            )}
            <button
              className="dfp-top-nav__utility-btn"
              onClick={handleDarkModeToggle}
              aria-label={`Switch to ${isDarkModeEnabled ? 'light' : 'dark'} mode`}
            >
              <FontAwesomeIcon icon={isDarkModeEnabled ? faMoon : faSun} />
            </button>
            <div className="avatar-dropdown">
              <img src={generateProfilePictureUrl(userAlias)} alt={userAlias} className="avatar-dropdown__image" />
              <div className="avatar-dropdown__trigger">
                <ButtonDropdown
                  variant="icon"
                  items={PROFILE_DROPDOWN_ITEMS}
                  expandToViewport
                  ariaLabel={`${userAlias} profile menu`}
                  onItemFollow={(e) => {
                    e.preventDefault();
                    if (e.detail.href) {
                      window.open(e.detail.href, '_blank', 'noopener,noreferrer');
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};
